import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaPlus, FaPen, FaMapMarkerAlt, FaTimes, FaArrowLeft } from "react-icons/fa"; // ✅ Imported
import { useNavigate } from "react-router-dom"; // ✅ Imported
import Loader from "../loader"; 
import "./Address.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
const MARS_MART_SOURCE = "mars-mart";

const isMarsMartAddress = (address) => {
  const source = String(
    address?.source || address?.address_source || address?.channel || ""
  ).toLowerCase();
  return source === MARS_MART_SOURCE;
};

export default function AddressList() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [detecting, setDetecting] = useState(false);
  
  const navigate = useNavigate(); // ✅ Hook initialized

  // Form State
  const [formData, setFormData] = useState({
    id: null,
    full_name: "",
    phone_number: "",
    street_address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "India",
    address_type: "HOME",
    is_default: false,
  });

  // 1. Fetch Addresses
  const fetchAddresses = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/address`, { withCredentials: true });
      const list = Array.isArray(res.data) ? res.data : [];
      setAddresses(list.filter((item) => !isMarsMartAddress(item)));
    } catch (err) {
      console.error("Error fetching addresses", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  // 2. Handle Inputs
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
  };

  // 3. 📍 Detect Location (GPS)
  const handleDetectLocation = () => {
    if (!navigator.geolocation) return alert("Geolocation is not supported by this browser.");
    
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`;
          const res = await axios.get(url);
          const addr = res.data.address;

          setFormData((prev) => ({
            ...prev,
            street_address: `${addr.road || ""} ${addr.suburb || ""} ${addr.neighbourhood || ""}`.trim(),
            city: addr.city || addr.town || addr.village || "",
            state: addr.state || "",
            postal_code: addr.postcode || "",
            country: addr.country || "India"
          }));
        } catch (err) {
          console.error("Geocoding error", err);
          alert("Could not fetch location details. Please enter manually.");
        } finally {
          setDetecting(false);
        }
      },
      (error) => {
        setDetecting(false);
        alert("Location permission denied.");
      }
    );
  };

  // 4. Submit (Add or Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await axios.put(`${BACKEND_URL}/address/${formData.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/address/add`, formData, { withCredentials: true });
      }
      setShowModal(false);
      fetchAddresses(); 
    } catch (err) {
      console.error("Save error", err);
      alert("Failed to save address.");
    }
  };

  // 5. Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this address?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/address/${id}`, { withCredentials: true });
      fetchAddresses();
    } catch (err) {
      console.error("Delete error", err);
    }
  };

  // 6. Set Default
  const handleSetDefault = async (addr) => {
    try {
      await axios.put(`${BACKEND_URL}/address/${addr.id}`, { ...addr, is_default: true }, { withCredentials: true });
      fetchAddresses();
    } catch (err) {
      console.error("Set Default error", err);
    }
  };

  // Helper to open modal
  const openModal = (addr = null) => {
    if (addr) {
      setFormData(addr);
    } else {
      setFormData({
        id: null, full_name: "", phone_number: "", street_address: "",
        city: "", state: "", postal_code: "", country: "India",
        address_type: "HOME", is_default: false,
      });
    }
    setShowModal(true);
  };

  if (loading) return <Loader />;

  return (
    <div className="address-container">
      
      {/* 🔥 THIS WAS MISSING IN YOUR FILE */}
      <div className="address-header">
        <button className="back-btn" onClick={() => navigate("/dashboard")}>
           <FaArrowLeft />
        </button>
        <h2>Your Addresses</h2>
      </div>
      {/* 🔥 END OF FIX */}

      <div className="address-grid">
        {/* === CARD 1: ADD NEW === */}
        <div className="add-new-card" onClick={() => openModal()}>
          <FaPlus className="plus-icon" />
          <span className="add-text">Add Address</span>
        </div>

        {/* === CARD 2+: ADDRESS LIST === */}
        {addresses.map((addr) => (
          <div key={addr.id} className={`address-card ${addr.is_default ? "default-card" : ""}`}>
            
            {addr.is_default && <span className="default-badge">Default</span>}

            <button className="edit-icon-btn" onClick={() => openModal(addr)} title="Edit Address">
              <FaPen />
            </button>

            <div className="addr-content">
              <div className="addr-name">{addr.full_name}</div>
              <div className="addr-text">{addr.street_address}</div>
              <div className="addr-text">
                {addr.city}, {addr.state} - <strong>{addr.postal_code}</strong>
              </div>
              <div className="addr-text">{addr.country}</div>
              <div className="addr-phone">Phone: {addr.phone_number}</div>
            </div>

            <div className="card-actions">
              {!addr.is_default ? (
                <button className="action-link" onClick={() => handleSetDefault(addr)}>
                  Set as Default
                </button>
              ) : (
                <span></span>
              )}
              
              <button className="action-link delete" onClick={() => handleDelete(addr.id)}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* === MODAL FORM === */}
      {showModal && (
        <div className="addr-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="addr-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={() => setShowModal(false)}>
              <FaTimes />
            </button>
            <h3 className="modal-title">{formData.id ? "Edit Address" : "Add New Address"}</h3>

            <button type="button" className="gps-btn" onClick={handleDetectLocation} disabled={detecting}>
              <FaMapMarkerAlt /> {detecting ? "Detecting location..." : "Use my current location"}
            </button>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Full Name</label>
                <input className="form-input" name="full_name" value={formData.full_name} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label>Mobile Number</label>
                <input className="form-input" name="phone_number" value={formData.phone_number} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label>Flat, House no., Building, Company, Apartment</label>
                <input 
                  className="form-input" 
                  name="street_address" 
                  value={formData.street_address} 
                  onChange={handleChange} 
                  required 
                  placeholder=""
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input className="form-input" name="city" value={formData.city} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input className="form-input" name="state" value={formData.state} onChange={handleChange} required />
                </div>
              </div>

              <div className="form-group">
                <label>Pincode</label>
                <input className="form-input" name="postal_code" value={formData.postal_code} onChange={handleChange} required />
              </div>

              <div className="form-group">
                 <label className="default-address-label">
                   <input type="checkbox" name="is_default" checked={formData.is_default} onChange={handleChange} />
                   Make this my default address
                 </label>
              </div>

              <button type="submit" className="save-btn">Save Address</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

